import { useMemo, useState, type FormEvent } from "react";
import type { Trip } from "../../lib/bookingApi";
import {
  formatLong,
  inRange,
  monthLabel,
  nightsBetween,
  todayIso,
} from "../../lib/dates";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const SOURCE_COLORS: Record<Trip["source"], string> = {
  direct: "bg-mars",
  outdoorsy: "bg-turquoise",
  rvshare: "bg-sun",
  rvezy: "bg-foam",
  other: "bg-zinc-400",
};

type ViewMode = "calendar" | "cards";
type SortKey = "soonest" | "latest" | "source" | "status";

interface Props {
  trips: Trip[];
  onCreate: (input: {
    startDate: string;
    endDate: string;
    status: Trip["status"];
    source: Trip["source"];
    guestLabel: string;
  }) => Promise<void>;
  onDelete: (trip: Trip) => Promise<void>;
}

function tripOnDay(trip: Trip, iso: string): boolean {
  return inRange(iso, trip.startDate, trip.endDate);
}

const TripBoard = ({ trips, onCreate, onDelete }: Props) => {
  const today = todayIso();
  const initial = new Date();
  const [view, setView] = useState<ViewMode>("calendar");
  const [cursor, setCursor] = useState({ year: initial.getFullYear(), month: initial.getMonth() });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Trip["status"]>("all");
  const [sourceFilter, setSourceFilter] = useState<"all" | Trip["source"]>("all");
  const [originFilter, setOriginFilter] = useState<"all" | Trip["origin"]>("all");
  const [sort, setSort] = useState<SortKey>("soonest");

  const [tripStart, setTripStart] = useState("");
  const [tripEnd, setTripEnd] = useState("");
  const [tripStatus, setTripStatus] = useState<Trip["status"]>("booked");
  const [tripSource, setTripSource] = useState<Trip["source"]>("direct");
  const [tripLabel, setTripLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const next = trips.filter((trip) => {
      if (statusFilter !== "all" && trip.status !== statusFilter) {
        return false;
      }
      if (sourceFilter !== "all" && trip.source !== sourceFilter) {
        return false;
      }
      if (originFilter !== "all" && trip.origin !== originFilter) {
        return false;
      }
      if (!needle) {
        return true;
      }
      const hay = [trip.guestLabel, trip.status, trip.source, trip.origin, trip.notes]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
    next.sort((a, b) => {
      if (sort === "latest") {
        return b.startDate.localeCompare(a.startDate) || b.endDate.localeCompare(a.endDate);
      }
      if (sort === "source") {
        return a.source.localeCompare(b.source) || a.startDate.localeCompare(b.startDate);
      }
      if (sort === "status") {
        return a.status.localeCompare(b.status) || a.startDate.localeCompare(b.startDate);
      }
      return a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate);
    });
    return next;
  }, [trips, query, statusFilter, sourceFilter, originFilter, sort]);

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const items: { iso: string | null; day: number | null }[] = [];
    for (let i = 0; i < startPad; i += 1) {
      items.push({ iso: null, day: null });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const iso = `${cursor.year}-${String(cursor.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      items.push({ iso, day });
    }
    while (items.length % 7 !== 0) {
      items.push({ iso: null, day: null });
    }
    return items;
  }, [cursor]);

  const dayTrips = selectedDay
    ? filtered.filter((trip) => tripOnDay(trip, selectedDay))
    : [];

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await onCreate({
        startDate: tripStart,
        endDate: tripEnd,
        status: tripStatus,
        source: tripSource,
        guestLabel: tripLabel,
      });
      setTripLabel("");
    } finally {
      setSaving(false);
    }
  }

  function TripCard({ trip }: { trip: Trip }) {
    const nights = nightsBetween(trip.startDate, trip.endDate);
    return (
      <div className="bg-white rounded-2xl p-4 border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold">
              {formatLong(trip.startDate)} → {formatLong(trip.endDate)}
            </div>
            <div className="text-sm text-zinc-500 mt-1">
              {nights} night{nights === 1 ? "" : "s"}
            </div>
          </div>
          <span className={`w-3 h-3 rounded-full mt-1 shrink-0 ${SOURCE_COLORS[trip.source]}`} />
        </div>
        <div className="flex flex-wrap gap-2 mt-3 text-xs">
          <span className="px-2 py-1 rounded-full bg-zinc-100">{trip.status}</span>
          <span className="px-2 py-1 rounded-full bg-zinc-100">{trip.source}</span>
          <span className="px-2 py-1 rounded-full bg-zinc-100">{trip.origin}</span>
        </div>
        {trip.guestLabel && <p className="text-sm text-zinc-700 mt-3">{trip.guestLabel}</p>}
        {trip.origin === "native" && (
          <button
            className="text-sm text-mars mt-3 underline"
            onClick={() => void onDelete(trip)}
          >
            Delete
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {(["calendar", "cards"] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              className={`px-4 py-2 rounded-2xl capitalize ${view === mode ? "bg-alt-blk text-white" : "bg-zinc-100"}`}
              onClick={() => setView(mode)}
            >
              {mode}
            </button>
          ))}
        </div>
        <div className="text-sm text-zinc-500">
          {filtered.length} of {trips.length} trip{trips.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search label, source…"
          className="border rounded-xl px-3 py-2"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="border rounded-xl px-3 py-2"
        >
          <option value="all">All statuses</option>
          <option value="hold">hold</option>
          <option value="booked">booked</option>
          <option value="blocked">blocked</option>
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as typeof sourceFilter)}
          className="border rounded-xl px-3 py-2"
        >
          <option value="all">All sources</option>
          <option value="direct">direct</option>
          <option value="outdoorsy">outdoorsy</option>
          <option value="rvshare">rvshare</option>
          <option value="rvezy">rvezy</option>
          <option value="other">other</option>
        </select>
        <select
          value={originFilter}
          onChange={(e) => setOriginFilter(e.target.value as typeof originFilter)}
          className="border rounded-xl px-3 py-2"
        >
          <option value="all">Native + imported</option>
          <option value="native">native only</option>
          <option value="import">imported only</option>
        </select>
      </div>

      {view === "cards" && (
        <div className="flex justify-end">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="border rounded-xl px-3 py-2"
          >
            <option value="soonest">Soonest first</option>
            <option value="latest">Latest first</option>
            <option value="source">Source</option>
            <option value="status">Status</option>
          </select>
        </div>
      )}

      {view === "calendar" ? (
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-2xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                className="px-3 py-1 rounded-xl hover:bg-zinc-100"
                onClick={() =>
                  setCursor((c) =>
                    c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 },
                  )
                }
              >
                ‹
              </button>
              <div className="font-semibold text-lg">{monthLabel(cursor.year, cursor.month)}</div>
              <button
                type="button"
                className="px-3 py-1 rounded-xl hover:bg-zinc-100"
                onClick={() =>
                  setCursor((c) =>
                    c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 },
                  )
                }
              >
                ›
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-zinc-500 mb-2">
              {WEEKDAYS.map((day) => (
                <div key={day}>{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((cell, index) => {
                if (!cell.iso) {
                  return <div key={`empty-${index}`} className="min-h-16" />;
                }
                const iso = cell.iso;
                const hits = filtered.filter((trip) => tripOnDay(trip, iso));
                const selected = selectedDay === iso;
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedDay(iso)}
                    className={[
                      "min-h-16 rounded-xl p-1 text-left border transition-colors",
                      selected ? "border-mars bg-sunset/10" : "border-transparent hover:bg-zinc-50",
                      iso === today ? "ring-1 ring-turquoise" : "",
                    ].join(" ")}
                  >
                    <div className={`text-sm ${iso < today ? "text-zinc-400" : "font-medium"}`}>
                      {cell.day}
                    </div>
                    <div className="flex flex-wrap gap-0.5 mt-1">
                      {hits.slice(0, 3).map((trip) => (
                        <span
                          key={trip.id}
                          className={`w-2 h-2 rounded-full ${SOURCE_COLORS[trip.source]}`}
                          title={trip.guestLabel || trip.source}
                        />
                      ))}
                      {hits.length > 3 && (
                        <span className="text-[10px] text-zinc-500">+{hits.length - 3}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-zinc-500 mt-4">
              {(Object.keys(SOURCE_COLORS) as Trip["source"][]).map((source) => (
                <span key={source} className="inline-flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${SOURCE_COLORS[source]}`} />
                  {source}
                </span>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold">
              {selectedDay ? formatLong(selectedDay) : "Select a day"}
            </h3>
            {selectedDay && dayTrips.length === 0 && (
              <p className="text-sm text-zinc-500">Nothing booked this day.</p>
            )}
            {dayTrips.map((trip) => (
              <TripCard key={trip.id} trip={trip} />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.length === 0 && <p className="text-zinc-500">No trips match these filters.</p>}
          {filtered.map((trip) => (
            <TripCard key={trip.id} trip={trip} />
          ))}
        </div>
      )}

      <form className="bg-white rounded-2xl p-6 border grid md:grid-cols-2 lg:grid-cols-3 gap-3" onSubmit={submit}>
        <h2 className="font-semibold text-xl md:col-span-2 lg:col-span-3">Add a trip</h2>
        <p className="text-sm text-zinc-500 md:col-span-2 lg:col-span-3">
          Return date is the morning they drop off (exclusive). Imported platform bookings are
          read-only.
        </p>
        <label className="block text-sm">
          Pickup
          <input
            type="date"
            required
            value={tripStart}
            onChange={(e) => setTripStart(e.target.value)}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          Return
          <input
            type="date"
            required
            value={tripEnd}
            onChange={(e) => setTripEnd(e.target.value)}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          Status
          <select
            value={tripStatus}
            onChange={(e) => setTripStatus(e.target.value as Trip["status"])}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          >
            <option value="hold">hold</option>
            <option value="booked">booked</option>
            <option value="blocked">blocked</option>
          </select>
        </label>
        <label className="block text-sm">
          Source
          <select
            value={tripSource}
            onChange={(e) => setTripSource(e.target.value as Trip["source"])}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          >
            <option value="direct">direct</option>
            <option value="outdoorsy">outdoorsy</option>
            <option value="rvshare">rvshare</option>
            <option value="rvezy">rvezy</option>
            <option value="other">other</option>
          </select>
        </label>
        <label className="block text-sm md:col-span-2">
          Internal label
          <input
            value={tripLabel}
            onChange={(e) => setTripLabel(e.target.value)}
            className="mt-1 w-full border rounded-xl px-3 py-2"
          />
        </label>
        <div className="flex items-end">
          <button
            className="bg-mars text-white px-5 py-3 rounded-2xl font-semibold"
            type="submit"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save trip"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TripBoard;
